import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLElement} resetButton
 * @property {HTMLElement[]} [resultsItems]
 * @property {HTMLElement} [recentlyViewedWrapper]
 * @property {HTMLElement[]} [recentlyViewedTitle]
 * @property {HTMLElement[]} [recentlyViewedItems]
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();
  #activeFetch = null;
  #numericCatalog = null;
  #numericCatalogPromise = null;
  #fullCatalog = null;
  #fullCatalogPromise = null;
  #embeddedSearchIndex = null;
  #embeddedSearchIndexById = null;
  #embeddedSearchIndexLoad = null;
  #numericModeActive = false;
  #numericResultsLimit = 8;
  #shuffleResultsLimit = 4;
  #defaultProductsMarkup = null;
  #emptySectionMarkup = null;
  #hasAppliedInitialShuffle = false;
  #jackpotLocalStorageKey = 'eg_search_jackpot_claimed_v2';
  #jackpotTestGroupLocalStorageKey = 'eg_search_jackpot_test_group';
  #jackpotAnimationLock = false;
  #jackpotSequenceDurationMs = 2320;
  #suppressSearchFocusUntil = 0;
  #releaseSearchFocusRaf = 0;
  #searchFocusRaf = 0;

  #jackpotGroupAliases = {
    dress: 'dress',
    dresses: 'dress',
    skirt: 'skirt',
    skirts: 'skirt',
    'pajama-set': 'pajama-set',
    'pajama-sets': 'pajama-set',
    pajama_set: 'pajama-set',
    pajama: 'pajama-set',
    pajamas: 'pajama-set',
    set: 'pajama-set',
    sets: 'pajama-set',
    robe: 'robe',
    robes: 'robe',
    top: 'top',
    tops: 'top',
  };

  #jackpotRewardMeta = {
    dress: {
      label: 'DRESS',
      title: 'DRESS DISCOUNT JACKPOT',
      codeDatasetKey: 'jackpotCodeDress',
      enabledDatasetKey: 'jackpotEnabledDress',
    },
    skirt: {
      label: 'SKIRT',
      title: 'SKIRT DISCOUNT JACKPOT',
      codeDatasetKey: 'jackpotCodeSkirt',
      enabledDatasetKey: 'jackpotEnabledSkirt',
    },
    'pajama-set': {
      label: 'PAJAMA SET',
      title: 'PAJAMA SET DISCOUNT JACKPOT',
      codeDatasetKey: 'jackpotCodePajamaSet',
      enabledDatasetKey: 'jackpotEnabledPajamaSet',
    },
    robe: {
      label: 'ROBE',
      title: 'ROBE DISCOUNT JACKPOT',
      codeDatasetKey: 'jackpotCodeRobe',
      enabledDatasetKey: 'jackpotEnabledRobe',
    },
    top: {
      label: 'TOP',
      title: 'TOP DISCOUNT JACKPOT',
      codeDatasetKey: 'jackpotCodeTop',
      enabledDatasetKey: 'jackpotEnabledTop',
    },
  };

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    this.#ensureShuffleStyles();

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    onDocumentLoaded(() => {
      this.resetSearch(false);
    });
  }

  handleModalClick = (event) => {
    this.#handleModalClick(event);
  };

  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (
      this.#shouldSuppressSearchFocus() ||
      target.closest('.eg-search-jackpot-reward') ||
      target.closest('.predictive-search-results__shuffle-button') ||
      target.closest('#predictive-search-products')
    ) {
      return;
    }

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement) {
      this.#scheduleSearchInputFocus();
    }
  };

  #handleShuffleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.#suppressSearchFocus();
    this.#scheduleReleaseSearchFocus();

    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;
    if (this.#jackpotAnimationLock) return;

    const elements = this.#getDefaultProductsSectionElements();
    if (!elements.root || !elements.productsWrapper) return;

    await this.#clearJackpotReward(elements.root, elements.productsWrapper, true);

    button.disabled = true;
    button.classList.add('is-loading');

    try {
      const catalog = await this.#getFullCatalog();
      const testGroup = this.#getJackpotTestGroup();
      const randomProducts = testGroup
        ? this.#pickJackpotTestProducts(catalog, testGroup, this.#shuffleResultsLimit)
        : this.#pickRandomProducts(catalog, this.#shuffleResultsLimit);

      elements.productsWrapper.innerHTML = this.#buildDefaultProductCardsMarkup(randomProducts);
      this.#applyImageLoadState(elements.productsWrapper);
      this.#cacheDefaultProductsMarkup();
      this.#hasAppliedInitialShuffle = true;
      this.#currentIndex = -1;
      this.#resetScrollPositions();

      const jackpotReward = this.#getJackpotReward(randomProducts);
      if (jackpotReward) {
        await this.#triggerJackpotReward(jackpotReward);
      }
    } catch (error) {
      console.error('Shuffle products failed', error);
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
      this.#suppressSearchFocus();
      this.#scheduleReleaseSearchFocus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();

    if (this.#releaseSearchFocusRaf) {
      cancelAnimationFrame(this.#releaseSearchFocusRaf);
      this.#releaseSearchFocusRaf = 0;
    }

    if (this.#searchFocusRaf) {
      cancelAnimationFrame(this.#searchFocusRaf);
      this.#searchFocusRaf = 0;
    }
  }

  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }

        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
      item.removeAttribute('aria-selected');
    });

    if (index < 0) {
      return;
    }

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex !== index) {
        continue;
      }

      item.setAttribute('aria-selected', 'true');

      if (this.#isKeyboardNavigation) {
        item.classList.add('keyboard-focus');
        item.scrollIntoView({
          behavior: prefersReducedMotion() ? 'instant' : 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
        this.#focusSearchInput();
      }

      break;
    }
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        this.#isKeyboardNavigation = true;
        event.preventDefault();

        if (event.shiftKey) {
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');

        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
          return;
        }

        if (this.#numericModeActive) {
          return;
        }

        const searchUrl = new URL(Theme.routes.search_url, location.origin);
        searchUrl.searchParams.set('q', this.refs.searchInput.value);
        searchUrl.searchParams.set('type', 'product');
        window.location.href = searchUrl.toString();
        break;
      }
    }
  };

  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;
    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.#focusSearchInput();
    }

    this.#resetSearch();
  }, 100);

  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();

    const numericQuery = this.#getNumericOnlyQuery(searchTerm);

    if (numericQuery) {
      void this.#renderNumericResults(numericQuery);
      return;
    }

    this.#setNumericMode(false);
    void this.#getSearchResults(searchTerm);
  }, 200);

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const trimmedTerm = searchTerm.trim();
    const numericCandidate = trimmedTerm.replace(/^[\s\-–—]+/, '');
    const isNumericOnly = /^\d+$/.test(numericCandidate);

    let queryTerm = trimmedTerm;

    if (isNumericOnly) {
      const normalized = String(parseInt(numericCandidate, 10));
      const padded3 = normalized.padStart(3, '0');

      if (normalized !== padded3) {
        queryTerm = `${normalized} OR ${padded3}`;
      } else {
        queryTerm = padded3;
      }
    }

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', queryTerm);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '8');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;
        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);
        this.#enhanceDefaultProductsSection();
        this.#applyImageLoadState(predictiveSearchResults);
        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  async #getEmptySectionRoot(url) {
    if (!this.#emptySectionMarkup) {
      const emptySectionMarkup = await sectionRenderer.getSectionHTML('predictive-search-empty', false, url);
      const parsedEmptySectionMarkup = new DOMParser()
        .parseFromString(emptySectionMarkup, 'text/html')
        .querySelector('.predictive-search-empty-section');

      if (!(parsedEmptySectionMarkup instanceof HTMLElement)) {
        throw new Error('No empty section markup found');
      }

      this.#emptySectionMarkup = parsedEmptySectionMarkup.outerHTML;
    }

    const cachedEmptySectionMarkup = new DOMParser()
      .parseFromString(this.#emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!(cachedEmptySectionMarkup instanceof HTMLElement)) {
      throw new Error('No cached empty section markup found');
    }

    return cachedEmptySectionMarkup;
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    this.#activeFetch = abortController;
    return abortController;
  }

  #getNumericOnlyQuery(searchTerm) {
    const trimmed = searchTerm.trim();
    const cleaned = trimmed.replace(/^[\s\-–—]+/, '');

    if (!/^\d+$/.test(cleaned)) {
      return null;
    }

    return cleaned;
  }

  #setNumericMode(isActive) {
    this.#numericModeActive = isActive;
  }

  #escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  #normalizeNumericTags(tagsValue) {
    let tags = [];

    if (Array.isArray(tagsValue)) {
      tags = tagsValue;
    } else if (typeof tagsValue === 'string') {
      tags = tagsValue.split(',');
    }

    return tags
      .map((tag) => String(tag).trim())
      .filter(Boolean)
      .filter((tag) => /^\d+$/.test(tag));
  }

  #getDisplayCodeFromTags(tagsValue) {
    const numericTags = this.#normalizeNumericTags(tagsValue);
    return [...numericTags].sort((a, b) => b.length - a.length)[0] || '';
  }

  #normalizeTags(tagsValue) {
    let tags = [];

    if (Array.isArray(tagsValue)) {
      tags = tagsValue;
    } else if (typeof tagsValue === 'string') {
      tags = tagsValue.split(',');
    }

    return tags
      .map((tag) => String(tag).trim())
      .filter(Boolean);
  }

  #normalizeJackpotGroup(value) {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/^jackpot\s*[:/_-]\s*/i, '')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9_-]/g, '');
  }

  #resolveJackpotGroup(value) {
    const normalized = this.#normalizeJackpotGroup(value);
    if (!normalized) return '';

    return this.#jackpotGroupAliases[normalized] || normalized;
  }

  #normalizeJackpotGroups(value) {
    let groups = [];

    if (Array.isArray(value)) {
      groups = value;
    } else if (typeof value === 'string') {
      groups = value.split(',');
    }

    return [...new Set(groups.map((group) => this.#resolveJackpotGroup(group)).filter(Boolean))];
  }

  #getProductType(product) {
    return String(product?.productType || product?.product_type || product?.type || '').trim();
  }

  #getProductJackpotGroups(product) {
    const productTypeGroup = this.#resolveJackpotGroup(this.#getProductType(product));
    if (productTypeGroup) {
      return [productTypeGroup];
    }

    const explicitGroups = this.#normalizeJackpotGroups(product?.jackpotGroups || product?.jackpot_groups);
    if (explicitGroups.length > 0) {
      return explicitGroups;
    }

    const tags = this.#normalizeTags(product?.tags);
    const groups = tags
      .map((tag) => {
        const match = String(tag).trim().match(/^jackpot\s*[:/_-]\s*(.+)$/i);
        return match ? this.#resolveJackpotGroup(match[1]) : '';
      })
      .filter(Boolean);

    return [...new Set(groups)];
  }

  #getDatasetString(key) {
    return String(this.dataset?.[key] || '').trim();
  }

  #isJackpotEnabled() {
    return String(this.dataset.jackpotEnabled || '').toLowerCase() === 'true';
  }

  #isMobileJackpotDisabled() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  #isJackpotGroupEnabled(group) {
    const normalizedGroup = this.#resolveJackpotGroup(group);
    const rewardMeta = this.#jackpotRewardMeta[normalizedGroup];

    if (!rewardMeta?.enabledDatasetKey) {
      return true;
    }

    const settingValue = String(this.dataset?.[rewardMeta.enabledDatasetKey] || '').trim().toLowerCase();

    if (!settingValue) {
      return true;
    }

    return settingValue === 'true';
  }

  #getConfiguredJackpotCode(group) {
    const normalizedGroup = this.#resolveJackpotGroup(group);
    const rewardMeta = this.#jackpotRewardMeta[normalizedGroup];

    const groupCode = rewardMeta?.codeDatasetKey ? this.#getDatasetString(rewardMeta.codeDatasetKey) : '';
    const fallbackCode = this.#getDatasetString('jackpotCode');

    return groupCode || fallbackCode;
  }

  #getJackpotDisplayLabel(group) {
    const normalizedGroup = this.#resolveJackpotGroup(group);
    const rewardMeta = this.#jackpotRewardMeta[normalizedGroup];

    if (rewardMeta?.label) {
      return rewardMeta.label;
    }

    return normalizedGroup.replace(/[-_]+/g, ' ').toUpperCase();
  }

  #getJackpotDisplayTitle(group) {
    const normalizedGroup = this.#resolveJackpotGroup(group);
    const rewardMeta = this.#jackpotRewardMeta[normalizedGroup];

    if (rewardMeta?.title) {
      return rewardMeta.title;
    }

    return `${this.#getJackpotDisplayLabel(normalizedGroup)} DISCOUNT JACKPOT`;
  }

  #getJackpotTestGroup() {
    try {
      return this.#resolveJackpotGroup(window.localStorage?.getItem(this.#jackpotTestGroupLocalStorageKey) || '');
    } catch {
      return '';
    }
  }

  #pickJackpotTestProducts(products, group, count) {
    const normalizedGroup = this.#resolveJackpotGroup(group);

    const matchingProducts = products.filter((product) => {
      return this.#getProductJackpotGroups(product).includes(normalizedGroup);
    });

    if (matchingProducts.length >= count) {
      return this.#pickRandomProducts(matchingProducts, count);
    }

    console.warn(`Not enough products for jackpot test group: ${normalizedGroup}`);
    return this.#pickRandomProducts(products, count);
  }

  #hasClaimedJackpot() {
    try {
      return Boolean(window.localStorage?.getItem(this.#jackpotLocalStorageKey));
    } catch {
      return true;
    }
  }

  #claimJackpot(reward) {
    try {
      window.localStorage?.setItem(
        this.#jackpotLocalStorageKey,
        JSON.stringify({
          claimed: true,
          code: reward.code,
          group: reward.group,
          claimedAt: Date.now(),
        })
      );
    } catch {
      // If localStorage is unavailable, the current session still continues without breaking search.
    }
  }

  #getJackpotReward(products) {
    if (!this.#isJackpotEnabled()) return null;
    if (this.#isMobileJackpotDisabled()) return null;
    if (this.#hasClaimedJackpot()) return null;
    if (!Array.isArray(products) || products.length !== this.#shuffleResultsLimit) return null;

    const groupsByProduct = products.map((product) => this.#getProductJackpotGroups(product));
    if (groupsByProduct.some((groups) => groups.length === 0)) return null;

    const commonGroups = groupsByProduct[0].filter((group) => groupsByProduct.every((groups) => groups.includes(group)));
    const group = commonGroups[0];
    if (!group) return null;
    if (!this.#isJackpotGroupEnabled(group)) return null;

    const code = this.#getConfiguredJackpotCode(group);
    if (!code) return null;

    return {
      group,
      code,
      label: this.#getJackpotDisplayLabel(group),
      title: this.#getJackpotDisplayTitle(group),
    };
  }

  #focusSearchInput() {
    const input = this.refs.searchInput;
    if (!(input instanceof HTMLInputElement)) return;

    try {
      input.focus({ preventScroll: true });
    } catch (_) {
      input.focus();
    }
  }

  #scheduleSearchInputFocus() {
    if (this.#shouldSuppressSearchFocus() || this.#searchFocusRaf) return;

    this.#searchFocusRaf = requestAnimationFrame(() => {
      this.#searchFocusRaf = 0;
      if (this.#shouldSuppressSearchFocus()) return;
      this.#focusSearchInput();
    });
  }

  #wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  #suppressSearchFocus(duration = 900) {
    this.#suppressSearchFocusUntil = Date.now() + duration;
  }

  #shouldSuppressSearchFocus() {
    return Date.now() < this.#suppressSearchFocusUntil;
  }

  #scheduleReleaseSearchFocus() {
    if (this.#releaseSearchFocusRaf) return;

    this.#releaseSearchFocusRaf = requestAnimationFrame(() => {
      this.#releaseSearchFocusRaf = 0;
      this.#releaseSearchFocus();
    });
  }

  #releaseSearchFocus() {
    const activeElement = document.activeElement;

    if (
      activeElement instanceof HTMLElement &&
      (activeElement === this.refs.searchInput || activeElement.closest('.predictive-search-results__shuffle-button'))
    ) {
      requestAnimationFrame(() => {
        if (document.activeElement === activeElement) {
          activeElement.blur();
        }
      });
    }
  }

  #blurSearchInputOnTouchDevice() {
    const isTouchDevice = window.matchMedia('(hover: none), (pointer: coarse), (max-width: 749px)').matches;

    if (!isTouchDevice) {
      return;
    }

    this.#releaseSearchFocus();
  }

  #copyText(text) {
    const value = String(text || '');

    if (navigator.clipboard?.writeText && window.isSecureContext) {
      return navigator.clipboard.writeText(value);
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand('copy');
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    } finally {
      textarea.remove();
    }
  }

#buildJackpotRewardMarkup(reward) {
  return `
    <div class="eg-search-jackpot-reward eg-search-jackpot-reward--no-image" role="status" aria-live="polite">
      <div class="eg-search-jackpot-reward__content">
        <p class="eg-search-jackpot-reward__title">${this.#escapeHtml(reward.label)} MATCH</p>
        <p class="eg-search-jackpot-reward__text">You hit the <span class="eg-search-jackpot-reward__text-strong">discount</span> jackpot.</p>
        <button class="eg-search-jackpot-reward__copy" type="button" data-eg-jackpot-copy="${this.#escapeHtml(reward.code)}">
          <span class="eg-search-jackpot-reward__code">${this.#escapeHtml(reward.code)}</span>
          <span class="eg-search-jackpot-reward__copy-label">
            <svg class="eg-search-jackpot-reward__copy-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path fill="currentColor" d="M4 2.5A1.5 1.5 0 0 1 5.5 1h6A1.5 1.5 0 0 1 13 2.5v6A1.5 1.5 0 0 1 11.5 10h-6A1.5 1.5 0 0 1 4 8.5zM5.5 2a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 .5.5h6a.5.5 0 0 0 .5-.5v-6a.5.5 0 0 0-.5-.5z"/>
              <path fill="currentColor" d="M2 5.5A1.5 1.5 0 0 1 3.5 4v1A.5.5 0 0 0 3 5.5v6a.5.5 0 0 0 .5.5h6a.5.5 0 0 0 .5-.5v-.5h1v.5A1.5 1.5 0 0 1 9.5 13h-6A1.5 1.5 0 0 1 2 11.5z"/>
            </svg>
            <span>COPY CODE</span>
          </span>
        </button>
        <p class="eg-search-jackpot-reward__notice">Copy it now — this code will not be shown again.</p>
      </div>
    </div>
  `;
}

  async #clearJackpotReward(root, productsWrapper, animate = true) {
    if (!(root instanceof HTMLElement) || !(productsWrapper instanceof HTMLElement)) return;

    const rewardElement = root.querySelector('.eg-search-jackpot-reward');

    if (rewardElement instanceof HTMLElement) {
      if (animate) {
        rewardElement.classList.add('is-leaving');
        await this.#wait(560);
      }

      rewardElement.remove();
    }

    root.style.removeProperty('--eg-search-jackpot-top');
    root.style.removeProperty('--eg-search-jackpot-left');
    root.style.removeProperty('--eg-search-jackpot-width');
    root.style.removeProperty('--eg-search-jackpot-height');

    productsWrapper.classList.remove(
      'eg-search-products-jackpot-target',
      'eg-search-products-jackpot-sequence',
      'eg-search-products-jackpot-filling',
      'eg-search-products-jackpot-winning'
    );
  }

  #positionJackpotReward(root, productsWrapper) {
    if (!(root instanceof HTMLElement) || !(productsWrapper instanceof HTMLElement)) return;

    const inset = 1;
    const top = productsWrapper.offsetTop - inset;
    const left = productsWrapper.offsetLeft - inset;
    const width = productsWrapper.offsetWidth + inset * 2;
    const height = productsWrapper.offsetHeight + inset * 2;

    requestAnimationFrame(() => {
      root.style.setProperty('--eg-search-jackpot-top', `${top}px`);
      root.style.setProperty('--eg-search-jackpot-left', `${left}px`);
      root.style.setProperty('--eg-search-jackpot-width', `${width}px`);
      root.style.setProperty('--eg-search-jackpot-height', `${height}px`);
    });
  }

  async #triggerJackpotReward(reward) {
    if (this.#jackpotAnimationLock) return;

    const { root, productsWrapper } = this.#getDefaultProductsSectionElements();
    if (!(root instanceof HTMLElement) || !(productsWrapper instanceof HTMLElement)) return;

    this.#jackpotAnimationLock = true;
    this.#claimJackpot(reward);

    try {
      await this.#clearJackpotReward(root, productsWrapper, false);

      productsWrapper.classList.add('eg-search-products-jackpot-target');
      productsWrapper.classList.add('eg-search-products-jackpot-sequence');

      await this.#wait(this.#jackpotSequenceDurationMs);

      productsWrapper.classList.remove('eg-search-products-jackpot-sequence');
      this.#positionJackpotReward(root, productsWrapper);
      root.insertAdjacentHTML('beforeend', this.#buildJackpotRewardMarkup(reward));
      productsWrapper.classList.add('eg-search-products-jackpot-filling');

      const copyButton = root.querySelector('[data-eg-jackpot-copy]');
      if (copyButton instanceof HTMLButtonElement) {
        copyButton.addEventListener('click', async (event) => {
          event.preventDefault();
          event.stopPropagation();

          const code = copyButton.dataset.egJackpotCopy || reward.code;
          const label = copyButton.querySelector('.eg-search-jackpot-reward__copy-label span');
          const rewardElement = copyButton.closest('.eg-search-jackpot-reward');
          const notice = rewardElement?.querySelector('.eg-search-jackpot-reward__notice');

          try {
            await this.#copyText(code);
            this.#blurSearchInputOnTouchDevice();

            if (label instanceof HTMLElement) label.textContent = 'COPIED';
            if (notice instanceof HTMLElement) notice.textContent = 'Copied. This reward will close shortly.';

            copyButton.classList.add('is-copied');

            if (rewardElement instanceof HTMLElement) {
              rewardElement.classList.add('is-copied', 'is-closing-soon');
            }

            await this.#wait(1400);
            await this.#clearJackpotReward(root, productsWrapper, true);
            this.#blurSearchInputOnTouchDevice();
          } catch (error) {
            if (label instanceof HTMLElement) label.textContent = 'COPY FAILED';
            console.error('Failed to copy jackpot discount code', error);
          }
        });
      }

      await this.#wait(980);
      productsWrapper.classList.add('eg-search-products-jackpot-winning');
      this.#blurSearchInputOnTouchDevice();
    } finally {
      this.#jackpotAnimationLock = false;
    }
  }

  /**
   * The product index is no longer embedded in every page. The first time search needs it,
   * it is fetched from the eg-search-index section and cached in sessionStorage for the visit.
   * The search page still embeds it (search-header), and then nothing is fetched.
   */
  #ensureEmbeddedSearchIndex() {
    if (document.getElementById('eg-search-numeric-index')) return Promise.resolve();
    if (this.#embeddedSearchIndexLoad) return this.#embeddedSearchIndexLoad;

    const cacheKey = `eg-search-index:${window.Shopify?.theme?.id || 'theme'}`;
    const maxAgeMs = 15 * 60 * 1000;

    const inject = (json) => {
      if (document.getElementById('eg-search-numeric-index')) return;

      const script = document.createElement('script');
      script.id = 'eg-search-numeric-index';
      script.type = 'application/json';
      script.textContent = json;
      document.body.appendChild(script);

      this.#embeddedSearchIndex = null;
      this.#embeddedSearchIndexById = null;
    };

    this.#embeddedSearchIndexLoad = (async () => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
        if (cached && typeof cached.json === 'string' && Date.now() - cached.savedAt < maxAgeMs) {
          inject(cached.json);
          return;
        }
      } catch {
        // Storage unavailable or corrupted: fall through to the network.
      }

      try {
        const url = new URL(window.Shopify?.routes?.root || '/', location.origin);
        url.searchParams.set('section_id', 'eg-search-index');

        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const json =
          new DOMParser().parseFromString(html, 'text/html').getElementById('eg-search-numeric-index')
            ?.textContent || '[]';

        inject(json);

        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ savedAt: Date.now(), json }));
        } catch {
          // Quota or privacy mode: the index still works for this page.
        }
      } catch (error) {
        console.error('Failed to load eg-search-index', error);
      } finally {
        this.#embeddedSearchIndexLoad = null;
      }
    })();

    return this.#embeddedSearchIndexLoad;
  }

  #getEmbeddedSearchIndex() {
    if (Array.isArray(this.#embeddedSearchIndex)) {
      return this.#embeddedSearchIndex;
    }

    const script = document.getElementById('eg-search-numeric-index');
    if (!(script instanceof HTMLScriptElement)) {
      this.#embeddedSearchIndex = [];
      this.#embeddedSearchIndexById = new Map();
      return this.#embeddedSearchIndex;
    }

    try {
      const parsed = JSON.parse(script.textContent || '[]');
      const index = Array.isArray(parsed) ? parsed : [];

      this.#embeddedSearchIndex = index;
      this.#embeddedSearchIndexById = new Map(
        index
          .filter((item) => item && typeof item === 'object' && item.id != null)
          .map((item) => [String(item.id), item])
      );

      return this.#embeddedSearchIndex;
    } catch (error) {
      console.error('Failed to parse eg-search-numeric-index', error);
      this.#embeddedSearchIndex = [];
      this.#embeddedSearchIndexById = new Map();
      return this.#embeddedSearchIndex;
    }
  }

  #getEmbeddedSearchIndexById() {
    if (this.#embeddedSearchIndexById instanceof Map) {
      return this.#embeddedSearchIndexById;
    }

    this.#getEmbeddedSearchIndex();
    return this.#embeddedSearchIndexById || new Map();
  }

  #buildEmbeddedCatalogProduct(item) {
    return {
      id: item.id,
      title: String(item.title || ''),
      url: String(item.url || '#'),
      image: String(item.image || ''),
      collectionCode: String(item.collection_code || ''),
      productType: String(item.product_type || item.productType || ''),
      tags: this.#normalizeTags(item.tags),
      jackpotGroups: this.#normalizeJackpotGroups(item.jackpot_groups),
    };
  }

  #embeddedCatalogHasJackpotData(products) {
    return products.some((product) => this.#getProductJackpotGroups(product).length > 0);
  }

  async #fetchProductsPage(page) {
    const url = new URL('/products.json', location.origin);
    url.searchParams.set('limit', '250');
    url.searchParams.set('page', String(page));

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to load products.json page ${page}`);
    }

    return response.json();
  }

  async #getNumericCatalog() {
    if (Array.isArray(this.#numericCatalog)) {
      return this.#numericCatalog;
    }

    if (this.#numericCatalogPromise) {
      return this.#numericCatalogPromise;
    }

    this.#numericCatalogPromise = (async () => {
      await this.#ensureEmbeddedSearchIndex();

      const collected = [];
      const maxPages = 12;
      const embeddedIndexById = this.#getEmbeddedSearchIndexById();

      for (let page = 1; page <= maxPages; page += 1) {
        const payload = await this.#fetchProductsPage(page);
        const products = Array.isArray(payload?.products) ? payload.products : [];

        if (products.length === 0) {
          break;
        }

        for (const product of products) {
          const indexEntry = embeddedIndexById.get(String(product.id));
          const numericTags = this.#normalizeNumericTags(product.tags);
          const embeddedCode = typeof indexEntry?.collection_code === 'string' ? indexEntry.collection_code.trim() : '';
          const displayCode = embeddedCode || [...numericTags].sort((a, b) => b.length - a.length)[0] || '';

          if (!numericTags.length && !embeddedCode) {
            continue;
          }

          const image =
            indexEntry?.image ||
            product?.images?.[0]?.src ||
            product?.image?.src ||
            '';

          collected.push({
            id: product.id,
            title: product.title || '',
            url: `/products/${product.handle}`,
            image,
            numericTags: embeddedCode ? [embeddedCode] : numericTags,
            collectionCode: displayCode,
          });
        }

        if (products.length < 250) {
          break;
        }
      }

      this.#numericCatalog = collected;
      this.#numericCatalogPromise = null;
      return collected;
    })();

    return this.#numericCatalogPromise;
  }

  async #getFullCatalog() {
    if (Array.isArray(this.#fullCatalog)) {
      return this.#fullCatalog;
    }

    if (this.#fullCatalogPromise) {
      return this.#fullCatalogPromise;
    }

    this.#fullCatalogPromise = (async () => {
      await this.#ensureEmbeddedSearchIndex();

      const embeddedIndex = this.#getEmbeddedSearchIndex();
      const embeddedProducts = Array.isArray(embeddedIndex)
        ? embeddedIndex
            .filter((item) => item && typeof item === 'object' && item.id != null)
            .map((item) => this.#buildEmbeddedCatalogProduct(item))
        : [];

      const needsJackpotData = this.#isJackpotEnabled() || Boolean(this.#getJackpotTestGroup());
      const embeddedHasJackpotData = this.#embeddedCatalogHasJackpotData(embeddedProducts);

      if (embeddedProducts.length > 0 && (!needsJackpotData || embeddedHasJackpotData)) {
        this.#fullCatalog = embeddedProducts;
        this.#fullCatalogPromise = null;
        return embeddedProducts;
      }

      const collected = [];
      const maxPages = 12;
      const embeddedIndexById = this.#getEmbeddedSearchIndexById();

      try {
        for (let page = 1; page <= maxPages; page += 1) {
          const payload = await this.#fetchProductsPage(page);
          const products = Array.isArray(payload?.products) ? payload.products : [];

          if (products.length === 0) {
            break;
          }

          for (const product of products) {
            const indexEntry = embeddedIndexById.get(String(product.id));
            const embeddedCode = typeof indexEntry?.collection_code === 'string' ? indexEntry.collection_code.trim() : '';
            const fallbackCode = this.#getDisplayCodeFromTags(product.tags);

            const image =
              indexEntry?.image ||
              product?.images?.[0]?.src ||
              product?.image?.src ||
              '';

            collected.push({
              id: product.id,
              title: indexEntry?.title || product.title || '',
              url: indexEntry?.url || `/products/${product.handle}`,
              image,
              collectionCode: embeddedCode || fallbackCode,
              productType: product.product_type || '',
              tags: this.#normalizeTags(product.tags),
              jackpotGroups: this.#normalizeJackpotGroups(indexEntry?.jackpot_groups),
            });
          }

          if (products.length < 250) {
            break;
          }
        }
      } catch (error) {
        console.error('Failed to load full product catalog for search shuffle', error);
      }

      if (collected.length > 0) {
        this.#fullCatalog = collected;
        this.#fullCatalogPromise = null;
        return collected;
      }

      this.#fullCatalog = embeddedProducts;
      this.#fullCatalogPromise = null;
      return embeddedProducts;
    })();

    return this.#fullCatalogPromise;
  }

  #getProductsHeadingText() {
    return 'PRODUCTS';
  }

  #getDefaultProductsSectionElementsFromRoot(root) {
    if (!(root instanceof HTMLElement)) {
      return { root: null, productsTitle: null, productsWrapper: null };
    }

    const titles = Array.from(root.querySelectorAll('.predictive-search-results__title'));
    const productsTitle =
      titles.find((title) => {
        const text = title.textContent?.trim().toUpperCase() || '';
        return text === 'PRODUCTS' || text.includes('PRODUCTS');
      }) || null;

    const productsWrapper =
      productsTitle?.nextElementSibling instanceof HTMLElement &&
      productsTitle.nextElementSibling.classList.contains('predictive-search-results__wrapper-products')
        ? productsTitle.nextElementSibling
        : null;

    if (productsWrapper instanceof HTMLElement) {
      productsWrapper.classList.add('eg-search-products-wrapper');
    }

    return {
      root,
      productsTitle,
      productsWrapper,
    };
  }

  #getDefaultProductsSectionElements() {
    return this.#getDefaultProductsSectionElementsFromRoot(
      this.refs.predictiveSearchResults.querySelector('#predictive-search-products')
    );
  }

  #cacheDefaultProductsMarkup() {
    const { productsWrapper } = this.#getDefaultProductsSectionElements();
    if (!(productsWrapper instanceof HTMLElement)) return;

    this.#defaultProductsMarkup = productsWrapper.innerHTML;
  }

  #restoreDefaultProductsMarkup(root = null) {
    if (!this.#defaultProductsMarkup) {
      return false;
    }

    const targetRoot =
      root instanceof HTMLElement ? root : this.refs.predictiveSearchResults.querySelector('#predictive-search-products');

    const { productsWrapper } = this.#getDefaultProductsSectionElementsFromRoot(targetRoot);
    if (!(productsWrapper instanceof HTMLElement)) {
      return false;
    }

    productsWrapper.innerHTML = this.#defaultProductsMarkup;
    return true;
  }

  #buildShopifySizedImageUrl(image, width = 560) {
    if (!image) return '';

    try {
      const url = new URL(image, location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      const separator = image.includes('?') ? '&' : '?';
      return `${image}${separator}width=${width}`;
    }
  }

  // Cards are rendered into the closed search dialog on page load; lazy images download only
  // once the dialog is open and they are visible, instead of on every page view.
  #buildProductImageMarkup(image, title, { eager = false } = {}) {
    const sizedImage = this.#escapeHtml(this.#buildShopifySizedImageUrl(image, 560));

    if (!sizedImage) {
      return `<div class="resource-card__image-placeholder" aria-hidden="true"><svg class="resource-card__image-placeholder-icon" viewBox="0 0 24 24" focusable="false"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg></div>`;
    }

    return `<img class="resource-card__image" src="${sizedImage}" alt="${title}" loading="${eager ? 'eager' : 'lazy'}" decoding="async" fetchpriority="low" width="560" height="700">`;
  }

  #applyImageLoadState(root = this.refs.predictiveSearchResults) {
    if (!(root instanceof HTMLElement)) return;

    const images = root.querySelectorAll('.predictive-search-resource-card .resource-card__media .resource-card__image');

    images.forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;

      const media = image.closest('.resource-card__media');
      if (!(media instanceof HTMLElement)) return;

      const markLoaded = () => {
        media.classList.add('is-image-ready');
      };

      if (image.complete && image.naturalWidth > 0) {
        markLoaded();
        return;
      }

      media.classList.remove('is-image-ready');
      image.addEventListener('load', markLoaded, { once: true });
      image.addEventListener(
        'error',
        () => {
          media.classList.add('is-image-ready');
        },
        { once: true }
      );
    });
  }

  async #prepareEmptyStateMarkup(root) {
    const { productsWrapper } = this.#getDefaultProductsSectionElementsFromRoot(
      root.querySelector('#predictive-search-products')
    );
    if (!(productsWrapper instanceof HTMLElement)) return;

    if (this.#hasAppliedInitialShuffle && this.#defaultProductsMarkup) {
      productsWrapper.innerHTML = this.#defaultProductsMarkup;
      return;
    }

    const catalog = await this.#getFullCatalog();
    const randomProducts = this.#pickRandomProducts(catalog, this.#shuffleResultsLimit);
    const markup = this.#buildDefaultProductCardsMarkup(randomProducts);

    productsWrapper.innerHTML = markup;
    this.#defaultProductsMarkup = markup;
    this.#hasAppliedInitialShuffle = true;
  }

  #buildShuffleIconMarkup() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path
          fill="currentColor"
          fill-rule="evenodd"
          d="M0 3.5A.5.5 0 0 1 .5 3H1c2.202 0 3.827 1.24 4.874 2.418.49.552.865 1.102 1.126 1.532.26-.43.636-.98 1.126-1.532C9.173 4.24 10.798 3 13 3v1c-1.798 0-3.173 1.01-4.126 2.082A9.6 9.6 0 0 0 7.556 8a9.6 9.6 0 0 0 1.317 1.918C9.828 10.99 11.204 12 13 12v1c-2.202 0-3.827-1.24-4.874-2.418A10.6 10.6 0 0 1 7 9.05c-.26.43-.636.98-1.126 1.532C4.827 11.76 3.202 13 1 13H.5a.5.5 0 0 1 0-1H1c1.798 0 3.173-1.01 4.126-2.082A9.6 9.6 0 0 0 6.444 8a9.6 9.6 0 0 0-1.317-1.918C4.172 5.01 2.796 4 1 4H.5a.5.5 0 0 1-.5-.5"
        ></path>
        <path
          fill="currentColor"
          d="M13 5.466V1.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384l-2.36 1.966a.25.25 0 0 1-.41-.192m0 9v-3.932a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384l-2.36 1.966a.25.25 0 0 1-.41-.192"
        ></path>
      </svg>
    `;
  }

  #ensureShuffleStyles() {
    if (document.getElementById('eg-predictive-search-shuffle-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'eg-predictive-search-shuffle-styles';
    style.textContent = `
      #predictive-search-products .predictive-search-results__title--with-shuffle {
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
      }

      #predictive-search-products .predictive-search-results__title-label {
        display: inline-flex;
        align-items: center;
        min-width: 0;
      }

      #predictive-search-products .predictive-search-results__shuffle-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        margin: 0;
        padding: 0;
        border: 0;
        border-radius: 0;
        background: transparent;
        color: currentColor;
        cursor: pointer;
        appearance: none;
        -webkit-appearance: none;
        box-shadow: none;
        outline: none;
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
      }

      #predictive-search-products .predictive-search-results__shuffle-button:hover,
      #predictive-search-products .predictive-search-results__shuffle-button:focus,
      #predictive-search-products .predictive-search-results__shuffle-button:focus-visible,
      #predictive-search-products .predictive-search-results__shuffle-button:active {
        background: transparent;
        color: currentColor;
        box-shadow: none;
        outline: none;
      }

      #predictive-search-products .predictive-search-results__shuffle-button svg {
        width: 14px;
        height: 14px;
        display: block;
        pointer-events: none;
      }

      #predictive-search-products .predictive-search-results__shuffle-button.is-loading {
        opacity: 0.55;
        pointer-events: none;
      }

      #predictive-search-products.predictive-search-results__products {
        position: relative;
        isolation: isolate;
        overflow: visible;
      }

      #predictive-search-products .eg-search-products-wrapper {
        position: relative;
        overflow: hidden;
      }

      #predictive-search-products.predictive-search-results__products > .eg-search-products-wrapper > .predictive-search-results__card--product {
        align-self: stretch !important;
      }

      #predictive-search-products.predictive-search-results__products .predictive-search-resource-card {
        grid-template-rows: auto calc(2.7em + 4px) !important;
      }

      #predictive-search-products.predictive-search-results__products .predictive-search-resource-card .resource-card__content {
        height: calc(2.7em + 4px) !important;
        min-height: calc(2.7em + 4px) !important;
        max-height: calc(2.7em + 4px) !important;
        box-sizing: border-box !important;
      }

      .predictive-search-resource-card .resource-card__title--search {
        height: 2.7em !important;
        min-height: 2.7em !important;
        max-height: 2.7em !important;
        text-transform: uppercase !important;
      }

      .predictive-search-resource-card .resource-card__media {
        position: relative;
        background: rgba(0, 0, 0, 0.03);
        overflow: hidden;
      }

      .predictive-search-resource-card .resource-card__media .resource-card__image {
        opacity: 0;
        transform: scale(1);
        transition: opacity 160ms ease;
        will-change: opacity, transform;
      }

      .predictive-search-resource-card .resource-card__media.is-image-ready .resource-card__image {
        opacity: 1;
      }

      #predictive-search-products .eg-search-products-jackpot-sequence > .predictive-search-results__card--product .resource-card__media .resource-card__image {
        animation: egSearchProductSlotPulse 560ms cubic-bezier(0.22, 1, 0.36, 1) both;
      }

      #predictive-search-products .eg-search-products-jackpot-sequence > .predictive-search-results__card--product:nth-child(1) .resource-card__media .resource-card__image {
        animation-delay: 0ms;
      }

      #predictive-search-products .eg-search-products-jackpot-sequence > .predictive-search-results__card--product:nth-child(2) .resource-card__media .resource-card__image {
        animation-delay: 520ms;
      }

      #predictive-search-products .eg-search-products-jackpot-sequence > .predictive-search-results__card--product:nth-child(3) .resource-card__media .resource-card__image {
        animation-delay: 1040ms;
      }

      #predictive-search-products .eg-search-products-jackpot-sequence > .predictive-search-results__card--product:nth-child(4) .resource-card__media .resource-card__image {
        animation-delay: 1560ms;
      }

      #predictive-search-products .eg-search-products-jackpot-sequence > .predictive-search-results__card--product {
        z-index: 1;
      }

      #predictive-search-products .eg-search-products-jackpot-filling > .predictive-search-results__card--product {
        pointer-events: none;
      }

      #predictive-search-products .eg-search-jackpot-reward {
        position: absolute;
        top: var(--eg-search-jackpot-top, 0);
        left: var(--eg-search-jackpot-left, 0);
        width: var(--eg-search-jackpot-width, 100%);
        height: var(--eg-search-jackpot-height, 100%);
        z-index: 50;
        display: grid;
        grid-template-columns: 1fr;
        min-height: 0;
        margin: 0;
        padding: 0;
        background: #8f0015;
        color: #fff;
        border: 0;
        box-sizing: border-box;
        overflow: hidden;
        pointer-events: auto;
        animation: egSearchJackpotFill 920ms cubic-bezier(0.22, 1, 0.36, 1) both;
      }

      #predictive-search-products .eg-search-jackpot-reward__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 16px;
        min-width: 0;
        padding: clamp(18px, 3vw, 44px);
        box-sizing: border-box;
        text-align: center;
        opacity: 0;
        transform: translateY(10px);
        animation: egSearchJackpotContentIn 520ms ease 520ms both;
      }

      #predictive-search-products .eg-search-jackpot-reward__title,
      #predictive-search-products .eg-search-jackpot-reward__text,
      #predictive-search-products .eg-search-jackpot-reward__notice {
        margin: 0;
        color: currentColor;
      }

      #predictive-search-products .eg-search-jackpot-reward__title {
        font-family: inherit;
        font-size: clamp(28px, 3.8vw, 56px);
        line-height: 0.96;
        font-weight: 400;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        opacity: 0.98;
      }

      #predictive-search-products .eg-search-jackpot-reward__text {
        font-size: clamp(15px, 1.45vw, 19px);
        line-height: 1.2;
        font-weight: 400;
        letter-spacing: -0.01em;
        opacity: 0.96;
        margin-block-start: -4px;
      }

      #predictive-search-products .eg-search-jackpot-reward__text-strong {
        font-weight: 700;
      }

#predictive-search-products .eg-search-jackpot-reward__notice {
  max-width: 360px;
  margin: -2px 0 0;
  color: currentColor;
  font-size: clamp(11px, 1vw, 13px);
  line-height: 1.25;
  font-weight: 400;
  letter-spacing: 0.01em;
  opacity: 0.68;
  transition:
    opacity 260ms ease,
    transform 260ms ease;
}

      #predictive-search-products .eg-search-jackpot-reward__copy {
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
        gap: 18px;
        width: min(100%, 374px);
        min-width: 0;
        margin: 10px 0 0;
        padding: 10px 12px 10px 22px;
        border: 0;
        border-radius: 0;
        background: #f8f0ef;
        color: #7f121a;
        cursor: pointer;
        appearance: none;
        -webkit-appearance: none;
        box-shadow: 0 10px 28px rgb(0 0 0 / 0.08);
        pointer-events: auto;
        transform: scale(1);
        transform-origin: center;
        transition:
          transform 180ms ease,
          box-shadow 180ms ease,
          background-color 180ms ease;
      }

      #predictive-search-products .eg-search-jackpot-reward__copy:hover,
      #predictive-search-products .eg-search-jackpot-reward__copy:focus-visible {
        background: #f8f0ef;
        color: #7f121a;
        outline: none;
        box-shadow: 0 14px 34px rgb(0 0 0 / 0.12);
        transform: scale(1.025);
      }

      #predictive-search-products .eg-search-jackpot-reward__copy:focus {
        background: #f8f0ef;
        color: #7f121a;
        outline: none;
        box-shadow: 0 10px 28px rgb(0 0 0 / 0.08);
      }

      #predictive-search-products .eg-search-jackpot-reward__copy:active {
        background: #f8f0ef;
        color: #7f121a;
        outline: none;
        box-shadow: 0 8px 22px rgb(0 0 0 / 0.08);
        transform: scale(0.992);
      }

      #predictive-search-products .eg-search-jackpot-reward__code {
        font-size: clamp(16px, 1.5vw, 20px);
        line-height: 1;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: #7f121a;
        white-space: nowrap;
      }

      #predictive-search-products .eg-search-jackpot-reward__copy-label {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        width: 132px;
        min-width: 132px;
        min-height: 38px;
        padding: 0 16px;
        border-radius: 0;
        background: #b00016;
        color: #fff;
        font-size: 12px;
        line-height: 1;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-align: center;
        text-transform: uppercase;
        opacity: 1;
        white-space: nowrap;
        box-sizing: border-box;
        transition:
          background-color 180ms ease,
          color 180ms ease;
      }

      #predictive-search-products .eg-search-jackpot-reward__copy-icon {
        width: 14px;
        height: 14px;
        display: block;
        flex: 0 0 auto;
      }

      #predictive-search-products .eg-search-jackpot-reward.is-copied .eg-search-jackpot-reward__copy-label {
        background: #7f121a;
      }

      #predictive-search-products .eg-search-jackpot-reward.is-copied .eg-search-jackpot-reward__copy {
        transform: scale(0.992);
      }

      #predictive-search-products .eg-search-jackpot-reward.is-closing-soon .eg-search-jackpot-reward__notice {
        opacity: 0.82;
        transform: translateY(2px);
      }

      #predictive-search-products .eg-search-jackpot-reward.is-closing-soon .eg-search-jackpot-reward__content {
        animation: egSearchJackpotCopiedSettle 1100ms ease both;
      }

      #predictive-search-products .eg-search-jackpot-reward.is-leaving {
        animation: egSearchJackpotFadeOut 560ms ease both;
      }

      #predictive-search-products .eg-search-jackpot-reward.is-leaving .eg-search-jackpot-reward__content {
        animation: egSearchJackpotContentFadeOut 420ms ease both;
      }

      @keyframes egSearchProductSlotPulse {
        0% {
          transform: scale(1);
          filter: brightness(1);
        }
        55% {
          transform: scale(1.105);
          filter: brightness(1.055);
        }
        100% {
          transform: scale(1.045);
          filter: brightness(1.02);
        }
      }

      @keyframes egSearchJackpotFill {
        0% {
          clip-path: inset(0 100% 0 0);
          transform: scale(0.99);
        }
        100% {
          clip-path: inset(0 0 0 0);
          transform: scale(1);
        }
      }

      @keyframes egSearchJackpotContentIn {
        0% {
          opacity: 0;
          transform: translateY(10px);
        }
        100% {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes egSearchJackpotCopiedSettle {
        0% {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        100% {
          opacity: 0.88;
          transform: translateY(0) scale(0.985);
        }
      }

      @keyframes egSearchJackpotFadeOut {
        0% {
          opacity: 1;
          transform: scale(1);
        }
        100% {
          opacity: 0;
          transform: scale(0.992);
        }
      }

      @keyframes egSearchJackpotContentFadeOut {
        0% {
          opacity: 1;
          transform: translateY(0);
        }
        100% {
          opacity: 0;
          transform: translateY(6px);
        }
      }

      @media (max-width: 749px) {
        #predictive-search-products .eg-search-jackpot-reward__content {
          gap: 12px;
          padding: 24px;
        }

        #predictive-search-products .eg-search-jackpot-reward__title {
          font-size: clamp(24px, 8vw, 38px);
          letter-spacing: 0.04em;
        }

        #predictive-search-products .eg-search-jackpot-reward__text {
          font-size: 14px;
        }

        #predictive-search-products .eg-search-jackpot-reward__notice {
          max-width: 300px;
          font-size: 11px;
        }

        #predictive-search-products .eg-search-jackpot-reward__copy {
          width: min(100%, 330px);
          min-width: 0;
          gap: 10px;
          padding: 9px 9px 9px 16px;
          border-radius: 0;
        }

        #predictive-search-products .eg-search-jackpot-reward__code {
          font-size: 14px;
          letter-spacing: 0.12em;
        }

        #predictive-search-products .eg-search-jackpot-reward__copy-label {
          width: 112px;
          min-width: 112px;
          min-height: 34px;
          padding: 0 12px;
          border-radius: 0;
          font-size: 10px;
        }

        #predictive-search-products .eg-search-jackpot-reward__copy-icon {
          width: 12px;
          height: 12px;
        }
      }
    `;

    document.head.appendChild(style);
  }

  #enhanceDefaultProductsSection() {
    const { productsTitle, productsWrapper } = this.#getDefaultProductsSectionElements();
    if (!(productsTitle instanceof HTMLElement)) return;

    if (productsWrapper instanceof HTMLElement) {
      productsWrapper.classList.add('eg-search-products-wrapper');
    }

    if (productsTitle.querySelector('.predictive-search-results__shuffle-button')) return;

    const labelText = productsTitle.textContent?.trim() || 'PRODUCTS';

    productsTitle.textContent = '';
    productsTitle.classList.add('predictive-search-results__title--with-shuffle');

    const label = document.createElement('span');
    label.className = 'predictive-search-results__title-label';
    label.textContent = labelText;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'predictive-search-results__shuffle-button';
    button.setAttribute('aria-label', 'Shuffle products');
    button.innerHTML = this.#buildShuffleIconMarkup();

    button.addEventListener('mousedown', (event) => {
      event.preventDefault();
      this.#suppressSearchFocus();
      this.#scheduleReleaseSearchFocus();
    });

    button.addEventListener(
      'pointerdown',
      () => {
        this.#suppressSearchFocus();
        this.#scheduleReleaseSearchFocus();
      },
      { passive: true }
    );

    button.addEventListener(
      'touchstart',
      () => {
        this.#suppressSearchFocus();
        this.#scheduleReleaseSearchFocus();
      },
      { passive: true }
    );

    button.addEventListener('focus', () => {
      if (this.#shouldSuppressSearchFocus()) {
        this.#scheduleReleaseSearchFocus();
      }
    });

    button.addEventListener('click', this.#handleShuffleClick);

    productsTitle.append(label, button);
  }

  async #applyInitialShuffleToDefaultProducts() {
    const elements = this.#getDefaultProductsSectionElements();
    if (!elements.productsWrapper) return;

    if (this.#hasAppliedInitialShuffle && this.#restoreDefaultProductsMarkup()) {
      this.#applyImageLoadState(elements.productsWrapper);
      this.#currentIndex = -1;
      return;
    }

    const catalog = await this.#getFullCatalog();
    const randomProducts = this.#pickRandomProducts(catalog, this.#shuffleResultsLimit);

    elements.productsWrapper.innerHTML = this.#buildDefaultProductCardsMarkup(randomProducts);
    this.#applyImageLoadState(elements.productsWrapper);
    this.#cacheDefaultProductsMarkup();
    this.#hasAppliedInitialShuffle = true;
    this.#currentIndex = -1;
  }

  #pickRandomProducts(products, count) {
    const pool = [...products];
    for (let i = pool.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }

    return pool.slice(0, count);
  }

  #buildDefaultProductCardsMarkup(products) {
    return products
      .map((product, indexPosition) => {
        const title = this.#escapeHtml(product.title || '');
        const url = this.#escapeHtml(product.url || '#');
        const image = this.#escapeHtml(product.image || '');
        const collectionCode = this.#escapeHtml(product.collectionCode || '');

        const imageMarkup = this.#buildProductImageMarkup(image, title);
        const codeMarkup = collectionCode
          ? `<span class="resource-card__collection-code">${collectionCode}</span><span class="resource-card__title-separator"> </span>`
          : '';

        return `
          <li
            class="predictive-search-results__card predictive-search-results__card--product"
            ref="resultsItems[]"
            data-search-result-index="search-results-${indexPosition + 1}"
          >
            <a
              class="predictive-search-resource-card resource-card resource-card--search"
              href="${url}"
              data-resource-type="product"
            >
              <div class="resource-card__media">
                ${imageMarkup}
              </div>
              <div class="resource-card__content">
                <p class="resource-card__title paragraph resource-card__title--search">
                  ${codeMarkup}${title}
                </p>
              </div>
            </a>
          </li>
        `;
      })
      .join('');
  }

  #buildNumericResultsMarkup(query, filteredProducts) {
    const titleText = this.#escapeHtml(this.#getProductsHeadingText());

    if (!filteredProducts.length) {
      return `
        <div class="predictive-search-results__inner" data-search-results>
          <p class="predictive-search-results__no-results">
            No products found for "${this.#escapeHtml(query)}"
          </p>
        </div>
      `;
    }

    const cardsMarkup = filteredProducts
      .map((product, indexPosition) => {
        const title = this.#escapeHtml(product.title || '');
        const url = this.#escapeHtml(product.url || '#');
        const image = this.#escapeHtml(product.image || '');
        const collectionCode = this.#escapeHtml(product.collectionCode || '');

        const imageMarkup = this.#buildProductImageMarkup(image, title);
        const codeMarkup = collectionCode
          ? `<span class="resource-card__collection-code">${collectionCode}</span><span class="resource-card__title-separator"> </span>`
          : '';

        return `
          <li
            class="predictive-search-results__card predictive-search-results__card--product"
            ref="resultsItems[]"
            data-search-result-index="search-results-${indexPosition + 1}"
          >
            <a
              class="predictive-search-resource-card resource-card resource-card--search"
              href="${url}"
              data-resource-type="product"
            >
              <div class="resource-card__media">
                ${imageMarkup}
              </div>
              <div class="resource-card__content">
                <p class="resource-card__title paragraph resource-card__title--search">
                  ${codeMarkup}${title}
                </p>
              </div>
            </a>
          </li>
        `;
      })
      .join('');

    const singleResultMarkup =
      filteredProducts.length === 1
        ? `<div data-single-result-url="${this.#escapeHtml(filteredProducts[0].url || '#')}"></div>`
        : '';

    return `
      <div class="predictive-search-results__inner" data-search-results>
        <div id="predictive-search-products" class="predictive-search-results__products" data-eg-numeric-search="true">
          <p class="predictive-search-results__title">${titleText}</p>
          <ul
            class="predictive-search-results__wrapper predictive-search-results__wrapper-products list-unstyled eg-search-products-wrapper"
            role="listbox"
            aria-label="${titleText}"
          >
            ${cardsMarkup}
          </ul>
          ${singleResultMarkup}
        </div>
      </div>
    `;
  }

  async #renderNumericResults(searchTerm) {
    this.#setNumericMode(true);
    this.#createAbortController();

    const { predictiveSearchResults } = this.refs;
    const query = searchTerm.trim();
    const catalog = await this.#getNumericCatalog();

    const filteredProducts = catalog
      .filter((product) => product.numericTags.some((tag) => tag.includes(query)))
      .sort((a, b) => {
        const aExact = a.numericTags.some((tag) => tag === query) ? 1 : 0;
        const bExact = b.numericTags.some((tag) => tag === query) ? 1 : 0;
        if (aExact !== bExact) return bExact - aExact;

        const aStarts = a.numericTags.some((tag) => tag.startsWith(query)) ? 1 : 0;
        const bStarts = b.numericTags.some((tag) => tag.startsWith(query)) ? 1 : 0;
        if (aStarts !== bStarts) return bStarts - aStarts;

        return 0;
      })
      .slice(0, this.#numericResultsLimit);

    predictiveSearchResults.innerHTML = this.#buildNumericResultsMarkup(query, filteredProducts);
    this.#applyImageLoadState(predictiveSearchResults);

    this.#currentIndex = -1;
    this.#resetScrollPositions();
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;

    this.#setNumericMode(false);
    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const parsedEmptySectionMarkup = await this.#getEmptySectionRoot(url);
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;

      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    await this.#prepareEmptyStateMarkup(parsedEmptySectionMarkup);

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#enhanceDefaultProductsSection();
    this.#applyImageLoadState(predictiveSearchResults);
    this.#cacheDefaultProductsMarkup();
    this.#currentIndex = -1;
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}